// @section 1 Campaigns / Replies — engaged-reply triage surface
// The operating surface for inbound replies: received human replies
// (engaged / redirect / not-interested) joined to their ops.reply_triage state.
// Read the queue (needs-attention first), then act per row: send a 1:1 reply
// (server sends via the Bridge Workspace SA from adam_caccamise@signal-rev.com),
// or mark it sent / skipped. Replaces "go to the dashboard/unibox to see who
// replied" — this is where the kirkw-type replies get worked from inside Atlas.

import { useEffect, useMemo, useState } from 'react';
import {
  getCampaignMastersOverview,
  getCampaignReplies,
  triageReply,
  sendCampaignReply,
  CampaignsUnconfigured,
  type CampaignMasterRow,
  type CampaignReply,
} from '../../api';

// @section 2 Helpers
function reText(subject: string | null): string {
  const s = (subject ?? '').trim();
  if (!s) return 'Re: (no subject)';
  return /^re:/i.test(s) ? s : `Re: ${s}`;
}
function textToHtml(body: string): string {
  const esc = body
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return esc.replace(/\n/g, '<br>');
}
function fmtTs(ts: string | null): string {
  if (!ts) return '—';
  const d = new Date(ts);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

type Draft = { subject: string; body: string };

// @section 3 Main
export default function Replies() {
  const [masters, setMasters] = useState<CampaignMasterRow[] | null>(null);
  const [replies, setReplies] = useState<CampaignReply[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [unconfigured, setUnconfigured] = useState(false);

  // Filters — default to the needs-attention queue
  const [masterKey, setMasterKey] = useState('');
  const [classification, setClassification] = useState('');
  const [status, setStatus] = useState('pending');
  const [search, setSearch] = useState('');

  // Row interaction
  const [openId, setOpenId] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);

  const handleErr = (e: unknown) => {
    if (e instanceof CampaignsUnconfigured) setUnconfigured(true);
    else setErr(String(e));
  };

  // @subsection 3.1 Master list for the campaign filter
  useEffect(() => {
    getCampaignMastersOverview().then((d) => setMasters(d.masters)).catch(handleErr);
  }, []);

  // @subsection 3.2 Load the queue on filter change / after an action
  useEffect(() => {
    setReplies(null); setErr(null);
    getCampaignReplies({
      masterKey: masterKey || undefined,
      classification: classification || undefined,
      status: status || undefined,
      search: search || undefined,
      limit: 300,
    }).then(setReplies).catch(handleErr);
  }, [masterKey, classification, status, search, reloadTick]);

  const pendingCount = useMemo(
    () => (replies ?? []).filter((r) => (r.response_status ?? 'pending') === 'pending').length,
    [replies],
  );

  // @subsection 3.3 Row expand — seed the composer from the triage suggestion (or blank)
  function toggleOpen(r: CampaignReply) {
    setActionErr(null);
    if (openId === r.email_id) { setOpenId(null); return; }
    setOpenId(r.email_id);
    setDrafts((d) => d[r.email_id] ? d : {
      ...d,
      [r.email_id]: {
        subject: r.suggested_subject || reText(r.subject),
        body: r.suggested_body || '',
      },
    });
  }
  function patchDraft(emailId: string, patch: Partial<Draft>) {
    setDrafts((d) => ({ ...d, [emailId]: { ...d[emailId], ...patch } }));
  }

  // @subsection 3.4 Actions
  async function doSend(r: CampaignReply) {
    const draft = drafts[r.email_id];
    if (!r.from_address) { setActionErr('No reply-to address on this message.'); return; }
    if (!draft?.body?.trim()) { setActionErr('Write a reply before sending.'); return; }
    setBusyId(r.email_id); setActionErr(null);
    try {
      await sendCampaignReply(r.email_id, {
        to: r.from_address,
        subject: draft.subject?.trim() || reText(r.subject),
        htmlBody: textToHtml(draft.body),
        textBody: draft.body,
      });
      setOpenId(null);
      setReloadTick((t) => t + 1);
    } catch (e) {
      setActionErr(`Send failed: ${String(e)}`);
    } finally {
      setBusyId(null);
    }
  }
  async function doMark(r: CampaignReply, s: 'sent' | 'skipped') {
    setBusyId(r.email_id); setActionErr(null);
    try {
      await triageReply(r.email_id, { status: s });
      if (openId === r.email_id) setOpenId(null);
      setReloadTick((t) => t + 1);
    } catch (e) {
      setActionErr(`Mark ${s} failed: ${String(e)}`);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="md:-mx-10 md:-my-8">
      <section className="bg-ink-950 text-paper-100 min-h-screen font-sans">
        <div className="px-4 py-5 md:px-10 md:py-7 max-w-[1600px] mx-auto">

          {/* @subsection 3.5 Breadcrumb */}
          <div className="flex items-center justify-between text-xs mb-6">
            <div className="text-muted">
              <span className="text-paper-100">Atlas</span>
              <span className="opacity-40"> / </span>
              <span className="text-paper-100/80">Campaigns</span>
              <span className="opacity-40"> / </span>
              <span className="text-paper-100/80">Replies</span>
            </div>
            <div className="flex items-center gap-2 text-[11px] text-ok">
              <span className="w-1.5 h-1.5 rounded-full bg-ok animate-pulse"></span>
              {unconfigured ? 'Pipeline not configured' : 'Live · daily 5 AM AZ refresh'}
            </div>
          </div>

          {/* @subsection 3.6 Title */}
          <div className="mb-6">
            <div className="text-[11px] uppercase tracking-[3px] text-muted mb-2">Engaged-reply triage</div>
            <h1 className="text-3xl md:text-5xl font-bold tracking-tight text-paper-100 mb-1">Replies</h1>
            <p className="text-sm text-muted">
              Human replies to the cold-email campaigns. Send a 1:1 reply from{' '}
              <span className="text-paper-100/80">adam_caccamise@signal-rev.com</span>, or mark it handled.
            </p>
          </div>

          {/* @subsection 3.7 Filter bar */}
          <div className="flex items-center gap-2 flex-wrap mb-5 text-xs">
            <Select value={status} onChange={setStatus} label="Status">
              <option value="pending">Needs attention</option>
              <option value="sent">Sent</option>
              <option value="skipped">Skipped</option>
              <option value="">All</option>
            </Select>
            <Select value={classification} onChange={setClassification} label="Type">
              <option value="">All replies</option>
              <option value="human_engaged">Engaged</option>
              <option value="human_redirect">Redirect</option>
              <option value="human_notinterested">Not interested</option>
            </Select>
            <Select value={masterKey} onChange={setMasterKey} label="Campaign">
              <option value="">All campaigns</option>
              {(masters ?? []).map((m) => (
                <option key={m.master_key} value={m.master_key}>{m.master_name}</option>
              ))}
            </Select>
            <input
              placeholder="Search from / subject / body…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="bg-ink-900 border border-ink-700 rounded px-3 py-1.5 text-paper-100 w-72"
            />
          </div>

          {actionErr && <div className="rounded-lg border border-stall/40 bg-ink-900 p-3 mb-4 text-stall text-xs">{actionErr}</div>}

          {/* @subsection 3.8 Queue */}
          {!replies && !err && !unconfigured && <Skeleton />}
          {err && <ErrCard msg={err} />}
          {unconfigured && (
            <div className="rounded-xl border-2 border-watch/40 bg-ink-900 p-5 text-sm text-watch">
              Backend returned 503 — awaiting first refresh.
            </div>
          )}
          {replies && replies.length === 0 && !err && (
            <EmptyCard text="No replies for this filter." />
          )}
          {replies && replies.length > 0 && (
            <div className="rounded-xl border border-ink-800 bg-ink-900/60 overflow-hidden">
              <div className="text-[10px] uppercase tracking-[2px] text-muted font-semibold px-4 pt-4 pb-2">
                {replies.length} replies · {pendingCount} need attention
              </div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-muted text-left border-b border-ink-800">
                    <th className="py-2 px-4">From</th>
                    <th>Subject</th>
                    <th>Type</th>
                    <th>Campaign</th>
                    <th>When</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {replies.map((r) => (
                    <ReplyRow
                      key={r.email_id}
                      r={r}
                      open={openId === r.email_id}
                      busy={busyId === r.email_id}
                      draft={drafts[r.email_id]}
                      onToggle={() => toggleOpen(r)}
                      onPatch={(p) => patchDraft(r.email_id, p)}
                      onSend={() => doSend(r)}
                      onMark={(s) => doMark(r, s)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}

        </div>
      </section>
    </div>
  );
}

// @section 4 Row (collapsed summary + expanded composer)
function ReplyRow({ r, open, busy, draft, onToggle, onPatch, onSend, onMark }: {
  r: CampaignReply; open: boolean; busy: boolean; draft: Draft | undefined;
  onToggle: () => void; onPatch: (p: Partial<Draft>) => void;
  onSend: () => void; onMark: (s: 'sent' | 'skipped') => void;
}) {
  return (
    <>
      <tr
        className={`border-t border-ink-800 cursor-pointer hover:bg-ink-800/40 ${open ? 'bg-ink-800/40' : ''}`}
        onClick={onToggle}
      >
        <td className="py-2 px-4 text-paper-100 align-top">{r.from_address ?? '—'}</td>
        <td className="text-muted align-top max-w-[320px] truncate">{r.subject ?? '—'}</td>
        <td className="align-top"><ClassBadge cls={r.classification} /></td>
        <td className="text-muted align-top">{r.master_name ?? r.campaign_name ?? '—'}</td>
        <td className="text-muted tabular-nums align-top">{fmtTs(r.timestamp_email)}</td>
        <td className="align-top"><StatusBadge status={r.response_status} /></td>
      </tr>
      {open && (
        <tr className="border-t border-ink-800 bg-ink-950/60">
          <td colSpan={6} className="px-4 py-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {/* Received reply */}
              <div>
                <div className="text-[10px] uppercase tracking-[2px] text-muted font-semibold mb-2">
                  Their reply {r.classification_confidence != null && (
                    <span className="text-muted/60">· conf {r.classification_confidence}</span>
                  )}
                </div>
                <div className="rounded-lg border border-ink-800 bg-ink-900 p-3 text-[13px] text-paper-100/90 whitespace-pre-wrap max-h-64 overflow-auto">
                  {r.body_text?.trim() || '(no body captured)'}
                </div>
              </div>
              {/* Composer */}
              <div onClick={(e) => e.stopPropagation()}>
                <div className="text-[10px] uppercase tracking-[2px] text-muted font-semibold mb-2">
                  Your reply {r.suggested_body && <span className="text-ok/70">· pre-filled from triage suggestion</span>}
                </div>
                <div className="flex items-center gap-2 mb-2 text-xs">
                  <span className="text-muted w-12">To</span>
                  <span className="text-paper-100/80">{r.from_address ?? '—'}</span>
                </div>
                <div className="flex items-center gap-2 mb-2 text-xs">
                  <span className="text-muted w-12">Subj</span>
                  <input
                    value={draft?.subject ?? ''}
                    onChange={(e) => onPatch({ subject: e.target.value })}
                    className="flex-1 bg-ink-900 border border-ink-700 rounded px-2 py-1.5 text-paper-100"
                  />
                </div>
                <textarea
                  value={draft?.body ?? ''}
                  onChange={(e) => onPatch({ body: e.target.value })}
                  rows={6}
                  placeholder="Write a reply… (sends from adam_caccamise@signal-rev.com)"
                  className="w-full bg-ink-900 border border-ink-700 rounded px-3 py-2 text-[13px] text-paper-100 mb-3"
                />
                <div className="flex items-center gap-2">
                  <button
                    onClick={onSend}
                    disabled={busy}
                    className="px-3 py-1.5 rounded bg-accent-500 text-ink-950 text-xs font-semibold hover:bg-accent-400 disabled:opacity-50"
                  >
                    {busy ? 'Sending…' : 'Send reply'}
                  </button>
                  <button
                    onClick={() => onMark('sent')}
                    disabled={busy}
                    className="px-3 py-1.5 rounded border border-ink-700 text-paper-100/80 text-xs hover:bg-ink-800 disabled:opacity-50"
                  >
                    Mark sent
                  </button>
                  <button
                    onClick={() => onMark('skipped')}
                    disabled={busy}
                    className="px-3 py-1.5 rounded border border-ink-700 text-muted text-xs hover:bg-ink-800 disabled:opacity-50"
                  >
                    Skip
                  </button>
                  {r.retarget_to && (
                    <span className="text-[10px] text-muted ml-2">retarget → {r.retarget_to}</span>
                  )}
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// @section 5 Presentational helpers
function Select({ value, onChange, label, children }: {
  value: string; onChange: (v: string) => void; label: string; children: React.ReactNode;
}) {
  return (
    <label className="flex items-center gap-1.5 text-muted">
      <span className="text-[10px] uppercase tracking-wide">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="bg-ink-900 border border-ink-700 rounded px-2 py-1.5 text-paper-100 max-w-[220px] truncate">
        {children}
      </select>
    </label>
  );
}
function ClassBadge({ cls }: { cls: string | null }) {
  const c = (cls ?? '').toLowerCase();
  if (c === 'human_engaged') return <Badge tone="accent">engaged</Badge>;
  if (c === 'human_redirect') return <Badge tone="watch">redirect</Badge>;
  if (c === 'human_notinterested') return <Badge tone="muted">not interested</Badge>;
  return <span className="text-muted text-[10px]">{cls ?? '—'}</span>;
}
function StatusBadge({ status }: { status: string | null }) {
  const s = (status ?? 'pending').toLowerCase();
  if (s === 'sent') return <Badge tone="ok">sent</Badge>;
  if (s === 'skipped') return <Badge tone="muted">skipped</Badge>;
  return <Badge tone="watch">needs attention</Badge>;
}
function Badge({ tone, children }: { tone: 'accent' | 'watch' | 'ok' | 'muted'; children: React.ReactNode }) {
  const cls = {
    accent: 'bg-accent-500/20 text-accent-400',
    watch: 'bg-watch/15 text-watch',
    ok: 'bg-ok/15 text-ok',
    muted: 'bg-ink-800 text-muted',
  }[tone];
  return <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wide font-semibold ${cls}`}>{children}</span>;
}
function Skeleton() {
  return (
    <div className="rounded-xl border border-ink-800 bg-ink-900/60 p-5 animate-pulse">
      <div className="h-4 w-40 bg-ink-800 rounded mb-3"></div>
      <div className="h-3 w-full bg-ink-800/60 rounded mb-2"></div>
      <div className="h-3 w-2/3 bg-ink-800/60 rounded"></div>
    </div>
  );
}
function ErrCard({ msg }: { msg: string }) {
  return <div className="rounded-xl border-2 border-stall/40 bg-ink-900 p-4 text-stall text-xs ring-1 ring-stall/10">{msg}</div>;
}
function EmptyCard({ text }: { text: string }) {
  return <div className="rounded-xl border border-ink-800 bg-ink-900/60 p-6 text-center text-muted text-sm">{text}</div>;
}
